// src/data/warRoomBoard.js
//
// SECTION 544 (2026-09-30). Bob, on the War Room as built: "This is not the war room I am imagining, I hope you have
// better plans than this." The plan put to him - the whole fight on one screen: the Board, the Clocks, the Patterns, the
// Money, the Carrier record, the Strategy, and the meetings moved to a tab - and his answer: "word".
//
// Pure. Everything here is READ from the committee's claims the General Chairperson can already read; nothing is
// computed that the record does not hold. Where a measure cannot be taken (no agreement clock on file, no answer
// date), it is reported as not measured - never as a pass or a fault (ruling 194).
import { LADDER } from './claimLadder';
import { deadlinesFromClaims } from './unionCalendar';
import { claimActs, carrierClockReference, answerBox, RULING_268_OUTCOME_BOX } from './offProperty';   // answerBox: ruling 268
import { carrierLimits } from './carrierAnswerLimits';
import { lastDayOf } from './timeLimit';   // ruling 314: day one is the day of the event
import { tallyClaims, claimBucket } from './claimTally';
import { rulesOf, dayDiff, today } from './reports';

const s = (v) => (v == null ? '' : String(v)).trim();
const isDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(s(v));
const num = (v) => { const n = Number(v); return v === '' || v == null || !isFinite(n) ? null : n; };
export const idOf = (c) => String((c && (c.docId || c.id)) || '');
export const labelOf = (c) => s(c && c.id) || s(c && c.title) || 'claim';

// ---- 1. THE BOARD -------------------------------------------------------------------------------------------------
const LABEL = Object.fromEntries(LADDER.map(r => [r.key, r.label]));
export const BOARD_COLUMNS = Object.freeze([
  { key: 'submitted', label: LABEL.submitted, office: 'LC' }, { key: 'intake', label: LABEL.intake, office: 'LC' },
  { key: 'investigation', label: LABEL.investigation, office: 'LC' }, { key: 'documentation_review', label: LABEL.documentation_review, office: 'LC' },
  { key: 'gc_review', label: LABEL.gc_review, office: 'GC' }, { key: 'valuation', label: LABEL.valuation, office: 'GC' },
  { key: 'negotiation', label: LABEL.negotiation, office: 'GC' }, { key: 'settlement', label: LABEL.settlement, office: 'GC' },
  { key: 'board', label: 'At the Board', office: 'NRAB / PLB' }, { key: 'closure', label: LABEL.closure, office: 'Closed' },
  { key: 'off', label: 'Suspended / archived', office: '' },
]);
export const URGENT_DAYS = 7;

// Which column a claim stands in. A claim with a notice to or submission at the Board and no award yet is AT THE BOARD,
// whatever rung it carries; an outcome of 'progressed_to_board' says the same.
export function columnOf(c) {
  if (!c || !c.status || c.status === 'draft') return null;
  if (c.status === 'suspended' || c.status === 'archived') return 'off';
  const acts = claimActs(c);
  const atBoard = acts.some(a => a.kind === 'board_notice' || a.kind === 'board_submitted') && !acts.some(a => a.kind === 'award_issued');
  if (claimBucket(c) === 'board' || (atBoard && c.status !== 'closure')) return 'board';
  return BOARD_COLUMNS.some(k => k.key === c.status) ? c.status : 'submitted';
}

// Every live deadline, with its days left from t. Overdue first, then soonest.
export function clockRows(claims, t) {
  const day = t || today();
  const live = (claims || []).filter(c => c && c.status && c.status !== 'draft' && c.status !== 'closure' && c.status !== 'archived');
  const byId = new Map(live.map(c => [idOf(c), c]));
  return deadlinesFromClaims(live).map(d => {
    const c = byId.get(String(d.claimId || '')) || null;
    const days = dayDiff(day, d.date);
    const carrierSide = d.party ? d.party === 'carrier' : /^Carrier answer due/.test(d.title);   // section 544: claimClocks carries the party
    const row = { date: d.date, days, title: d.title, claimId: String(d.claimId || ''), claim: c, side: carrierSide ? 'carrier' : 'organization' };
    // THE CARRIER DEFAULT FLAG: the carrier's time to answer has run and no answer is on the record. What the agreement
    // says follows is quoted verbatim from its own clock (carrierClockReference) - or, where the platform holds no such
    // words, the flag says so. It is a flag for the officer to act on, not a finding that the claim is won.
    if (row.side === 'carrier' && days < 0) {
      const ref = c ? carrierClockReference(c) : null;
      const words = ref && ref.clocks.find(k => k.onSilence);
      row.default = { since: -days, onSilence: words ? words.onSilence : null, cite: words ? (words.cite || ref.claimsRule) : null };
    }
    return row;
  }).sort((a, b) => a.date.localeCompare(b.date) || a.title.localeCompare(b.title));
}

export function boardCards(claims, t) {
  const day = t || today();
  const clocks = clockRows(claims, day);
  const next = new Map();
  for (const r of clocks) if (!next.has(r.claimId)) next.set(r.claimId, r);   // clockRows is sorted: first is soonest
  const cols = Object.fromEntries(BOARD_COLUMNS.map(k => [k.key, []]));
  for (const c of claims || []) {
    const col = columnOf(c);
    if (!col) continue;
    const n = next.get(idOf(c)) || null;
    cols[col].push({
      id: idOf(c), label: labelOf(c), claimant: s(c.claimantName), rules: rulesOf(c), amount: num(c.amountClaimed),
      carrier: s(c.railroad), office: s(c.scopeKey), next: n ? { date: n.date, days: n.days, title: n.title, side: n.side } : null,
      urgent: !!(n && n.days < URGENT_DAYS), overdue: !!(n && n.days < 0), claim: c,
    });
  }
  for (const k of Object.keys(cols)) cols[k].sort((a, b) => (a.next ? a.next.days : 1e9) - (b.next ? b.next.days : 1e9) || a.label.localeCompare(b.label));
  return { columns: BOARD_COLUMNS.map(k => ({ ...k, cards: cols[k.key] })), total: Object.values(cols).reduce((n, x) => n + x.length, 0),
    urgent: Object.values(cols).flat().filter(x => x.urgent).length, defaults: clocks.filter(r => r.default).length };
}

// ---- 3. THE PATTERNS -----------------------------------------------------------------------------------------------
// The same rule, the same place, the same carrier officer, the same employee - twice or more is a pattern. Each group
// carries its claims and how they stand. Nothing is inferred about why.
export const PATTERN_DIMENSIONS = Object.freeze([
  { key: 'rule', label: 'By rule', of: (c) => rulesOf(c) },
  { key: 'location', label: 'By location', of: (c) => [s(c.eventLocation) || s(c.workLocation)].filter(Boolean) },
  { key: 'officer', label: 'By carrier officer presented to', of: (c) => [s(c.presentedTo)].concat(claimActs(c).filter(a => a.kind === 'appealed').map(a => s(a.toOfficer))).filter(Boolean) },
  { key: 'employee', label: 'By employee who did the work', of: (c) => [s(c.violatorName)].filter(Boolean) },
]);
const norm = (x) => x.toLowerCase().replace(/[\s.,]+/g, ' ').trim();
export function patterns(claims, minCount) {
  const min = minCount || 2;
  const live = (claims || []).filter(c => c && c.status && c.status !== 'draft');
  return PATTERN_DIMENSIONS.map(d => {
    const groups = new Map();
    for (const c of live) {
      for (const v of Array.from(new Set(d.of(c).map(norm))).filter(Boolean)) {
        if (!groups.has(v)) groups.set(v, { key: v, label: d.of(c).find(x => norm(x) === v) || v, claims: [] });
        groups.get(v).claims.push(c);
      }
    }
    const rows = Array.from(groups.values()).filter(g => g.claims.length >= min).map(g => {
      const b = g.claims.map(claimBucket);
      return { ...g, n: g.claims.length, won: b.filter(x => x === 'awarded').length, lost: b.filter(x => x === 'unsuccessful').length,
        open: b.filter(x => x === 'open' || x === 'board').length, amount: g.claims.reduce((t, c) => t + (num(c.amountClaimed) || 0), 0) };
    }).sort((a, b) => b.n - a.n || a.label.localeCompare(b.label));
    return { ...d, rows, measured: live.some(c => d.of(c).length) };
  });
}

// ---- 4. THE MONEY -------------------------------------------------------------------------------------------------
// claimTally's reading per carrier and per rule, plus AT RISK: the amount claimed on open claims with an
// Organization deadline (appeal, Board) within 7 days or already past - money lost if the Organization misses its time.
export function money(claims, t) {
  const day = t || today();
  const live = (claims || []).filter(c => c && c.status && c.status !== 'draft');
  const orgSoon = new Set(clockRows(live, day).filter(r => r.side === 'organization' && r.days < URGENT_DAYS).map(r => r.claimId));
  const atRisk = live.filter(c => claimBucket(c) === 'open' && orgSoon.has(idOf(c)));
  const by = (keyOf) => {
    const m = new Map();
    for (const c of live) for (const k of keyOf(c)) { if (!m.has(k)) m.set(k, []); m.get(k).push(c); }
    return Array.from(m.entries()).map(([k, cs]) => ({ key: k, n: cs.length, tally: tallyClaims(cs),
      atRisk: cs.filter(c => atRisk.includes(c)).reduce((x, c) => x + (num(c.amountClaimed) || 0), 0) }))
      .sort((a, b) => b.tally.submitted.amount - a.tally.submitted.amount || a.key.localeCompare(b.key));
  };
  return {
    total: tallyClaims(live),
    atRisk: { n: atRisk.length, amount: atRisk.reduce((x, c) => x + (num(c.amountClaimed) || 0), 0), claims: atRisk },
    byCarrier: by(c => [s(c.railroad) || '(no carrier on the claim)']),
    byRule: by(c => { const r = rulesOf(c); return r.length ? r : ['(no rule on the claim)']; }),
  };
}

// ---- 5. THE CARRIER RECORD ----------------------------------------------------------------------------------------
// Each carrier officer a claim was presented to: served, answered (denied / allowed), answered LATE, NO ANSWER past the
// agreement's time, awaiting. Late and past-due are measured only where the agreement's answer time is on file
// (carrierAnswerLimits) and the claim carries its service date; otherwise the claim is counted as not measured.
export function carrierRecord(claims, t) {
  const day = t || today();
  const m = new Map();
  for (const c of claims || []) {
    if (!c || !c.status || c.status === 'draft') continue;
    const officer = s(c.presentedTo);
    if (!officer) continue;
    const k = norm(officer);
    if (!m.has(k)) m.set(k, { officer, carrier: s(c.railroad), served: 0, denied: 0, allowed: 0, partly: 0, amountAllowed: 0, amountAllowedOn: 0, late: 0, pastDue: 0, awaiting: 0, notMeasured: 0, claims: [] });
    const r = m.get(k);
    r.served++; r.claims.push(c);
    const acts = claimActs(c);
    const ans = acts.find(a => a.kind === 'carrier_answered');
    if (RULING_268_OUTCOME_BOX) {
      // Ruling 268 (2026-10-02): the one outcome box is what is counted - Granted, Partially granted and denied, Denied -
      // three separate columns; a partial is no longer folded into "allowed". The amount allowed is summed only where a
      // figure was recorded (amountAllowedOn says how many); a blank is unknown, never $0.
      const box = ans ? answerBox(ans.outcome) : null;
      if (box === 'denied') r.denied++;
      else if (box === 'granted') r.allowed++;
      else if (box === 'partly') r.partly++;
      if (ans && typeof ans.amountAllowed === 'number' && isFinite(ans.amountAllowed)) { r.amountAllowed += ans.amountAllowed; r.amountAllowedOn++; }
    } else {
      if (ans && /^denied/.test(s(ans.outcome))) r.denied++;
      if (ans && /^allowed/.test(s(ans.outcome))) r.allowed++;
    }
    const lim = carrierLimits(c.applicableAgreement);
    const days = lim && Number.isFinite(lim.carrierAnswerDays) ? lim.carrierAnswerDays : null;
    if (!isDate(c.presentedOn) || days === null) { r.notMeasured++; continue; }
    const due = lastDayOf(s(c.presentedOn), days);   // ruling 314
    const answeredOn = ans && isDate(ans.on) ? ans.on : (isDate(c.carrierAnsweredOn) ? s(c.carrierAnsweredOn) : '');
    if (answeredOn) { if (answeredOn > due) r.late++; }
    else if (c.outcome) r.notMeasured++;                 // decided with no answer date on the record
    else if (due < day) r.pastDue++;
    else r.awaiting++;
  }
  return Array.from(m.values()).sort((a, b) => (b.late + b.pastDue) - (a.late + a.pastDue) || b.served - a.served || a.officer.localeCompare(b.officer));
}

// ---- 6. THE STRATEGY NOTE -----------------------------------------------------------------------------------------
export const NOTE_MAX = 8000;
export function notePatch(claim, committee, user, input, existing) {
  const i = input || {};
  const notes = s(i.notes), nextMove = s(i.nextMove), precedent = s(i.precedent);
  const errors = [];
  if (!claim || !idOf(claim)) errors.push('No claim chosen.');
  if (!committee) errors.push('No committee.');
  if (!user || !user.uid) errors.push('Not signed in.');
  if (!notes && !nextMove && !precedent) errors.push('Write a note, a next move or the precedent before saving.');
  if (notes.length > NOTE_MAX || nextMove.length > 1000 || precedent.length > 2000) errors.push('Too long - the note holds ' + NOTE_MAX + ' characters, the next move 1,000, the precedent 2,000.');
  if (errors.length) return { ok: false, errors };
  const prev = existing && Array.isArray(existing.history) ? existing.history : [];
  const entry = { notes, nextMove, precedent, by: user.uid, byName: s(user.name || user.displayName || user.email), at: new Date().toISOString() };
  return { ok: true, record: { committee, claimId: idOf(claim), claimLabel: labelOf(claim), notes, nextMove, precedent,
    updatedBy: user.uid, updatedByName: entry.byName, history: prev.concat([entry]) } };
}
